import { EventMeta } from '../../../interfaces/meta.interface';

type FileAccessedFields = {
    ObjectId: string;
};

const FileAccessed: EventMeta<FileAccessedFields> = {
    title: 'SharePoint File Accessed',
    default: {
        description: 'SharePoint File Accessed',
        longDescription: 'A user or system account viewed a file.',
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        return {
            ObjectId: log.ObjectId,
        };
    },
    classifications: [
        // file accessed: fraudulent IP. not in allowed list.
        {
            severity: 'danger',
            description: (fields) => `A suspicious IP accessed a OneDrive file`,
            longDescription: (fields) =>
                `A file on OneDrive was accessed from a suspicious IP address that is not in your allowed list. This activity may indicate an attempt to view or download sensitive information without authorization.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file accessed: fraudulent IP.  allowed IP
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed IP list accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on OneDrive. The action originated from a suspicious IP that belongs to your allowed IP list. While the IP range itself is normally trusted, the individual IP has been flagged as suspicious, making this access unusual. Review the file access to confirm whether it was legitimate, particularly if the file is sensitive or if there are repeated attempts from suspicious IPs within trusted ranges.`,
            predicates: {
                isAllowedIP: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file accessed: fraudulent IP. allowed region.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed region accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on OneDrive. The action originated from a suspicious IP located inside your allowed region. While the geographic location itself is normally trusted, the specific IP address has been flagged as suspicious, making the access noteworthy. Review the activity to confirm whether the access was legitimate or potentially unauthorized, particularly if sensitive files were involved or if repeated access attempts are observed from flagged IPs.`,
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file accessed: fraudulent IP. in allowed countries.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed countries accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on OneDrive. The action originated from a suspicious IP located in one of your allowed countries. While the country itself is normally trusted under your policies, the individual IP has been flagged as suspicious. Review the activity to determine whether the access was legitimate, particularly if the file is sensitive or if there are repeated accesses from similar flagged sources.`,
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file accessed: fraudulent IP. in allowed asn.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed ASN list accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on OneDrive. The action originated from a suspicious IP included in your allowed ASN list. Although the ASN itself is trusted, the individual IP has been flagged as suspicious, making this access notable. Review the activity to determine whether the access was legitimate, particularly if the file is sensitive or if repeated attempts are observed from flagged IPs within the trusted ASN.`,
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file accessed: user in org. not in allowed list
        {
            severity: 'warning',
            description: (fields) =>
                `An unapproved IP outside your allowed ASN, IPs, region, or country accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on OneDrive. The action originated from an unapproved IP outside your allowed ASN, IPs, region, or country. Since this access came from outside all of your approved network and geographic boundaries, it is unusual and potentially risky. Review the file access carefully to determine whether it was expected or could indicate an attempt to reach OneDrive resources from an unauthorized location.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // file accessed: user not in org. not in allowed list
        {
            severity: 'info',
            description: (fields) => `An unapproved IP used a non-org account to access a file`,
            longDescription: (fields) =>
                `A file was accessed on OneDrive with an account that does not belong to your organisation. The action originated from an unapproved IP outside your allowed list. While file access is common, the combination of an external account and an unapproved IP makes this activity notable. Review the access details to confirm whether the activity was expected or potentially unauthorized, particularly if sensitive files were involved.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // file accessed: microsoft activity
        {
            severity: 'info',
            description: (fields) => `A Microsoft system operation (synchronization server IP) accessed a file`,
            longDescription: (fields) =>
                `A file was accessed on OneDrive. This is an informational record of a standard activity event. No unusual conditions were associated with this entry. No immediate action is required unless the file access was unexpected or aligns with other anomalies.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        // file accessed
        {
            severity: 'info',
            description: (fields) => `A file was accessed on OneDrive`,
            longDescription: (fields) =>
                `A file was accessed on OneDrive. This is an informational record of a standard activity event. No unusual conditions were associated with this entry. No immediate action is required unless the file access was unexpected or aligns with other anomalies.`,
        },
    ],
};

export default FileAccessed;
